import { useMemo, useState } from "react";
import { Button, Card, Collapse, Divider, Group, Stack, Text, Textarea, Title } from "@mantine/core";
import { IconCalendarEvent, IconCode, IconPlayerPlay } from "@tabler/icons-react";
import { fmtNumber } from "@passio/api-client";
import { DemoChains, mealPlanFromExecution, runTokens, sinkOutput, withPlanRequirements, type ResolvedChain } from "@passio/easyai-client";
import { useSession } from "../lib/session";
import { Eyebrow, PageHeader } from "../theme/components";
import { EmptyState, RequireFeature } from "../components/common";
import { JsonView } from "../components/JsonView";
import { ChainError, RunProgressCard, useChainRun, type RunStage } from "../components/ChainRun";
import { ChainForm, initialValues, missingRequired, toInputData, type ChainFormValues } from "../components/ChainForm";
import { MealPlanView } from "../components/MealPlanView";

/**
 * Every meal-plan chain runs the same pipeline; Family has one extra household step up front, so its
 * node numbers are shifted by one. Offsetting from the result node keeps both progress views identical.
 */
function planStages(chain: ResolvedChain, pdf: boolean): RunStage[] {
  const offset = Math.max(0, Number(chain.sinkNodeId?.match(/(\d+)$/)?.[1] ?? 9) - 9);
  const stages: RunStage[] = [
    { label: "Reading your request and planning the days", throughNode: 1 + offset },
    { label: "Writing each day's recipes", throughNode: 2 + offset },
    { label: "Matching ingredients to Passio foods", throughNode: 5 + offset },
    { label: "Adding nutrition and macro totals", throughNode: 8 + offset },
    { label: "Assembling your plan", throughNode: 9 + offset },
  ];
  if (pdf) stages.push({ label: "Making the printable PDF", throughNode: 10 + offset });
  return stages;
}
const GENERIC_STAGES: RunStage[] = [{ label: "Running the chain", throughNode: Number.MAX_SAFE_INTEGER }];

const ORDER = Object.keys(DemoChains);
const SAMPLE_INPUT = { user_request: "3 days, vegetarian, high protein", days: 3 };
const jsonFor = (c?: ResolvedChain) => JSON.stringify(c && Object.keys(c.defaultInput).length ? c.defaultInput : SAMPLE_INPUT, null, 2);

/**
 * Plan types (`DemoChains`) run through the Passio API meal plan routes, which add the plan
 * requirements and pick the EasyAI chain. Custom chains added in Admin → Chains run on EasyAI
 * directly from raw JSON. Plan types without a real chain yet show as coming soon.
 */
export function MealPlansPage() {
  const { me } = useSession();
  const all = useMemo(() => me?.app.easyAi.chains ?? [], [me]);
  const planTypes = useMemo(() => all.filter((c) => c.group === "meal_plan").sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key)), [all]);
  const custom = useMemo(() => all.filter((c) => !c.group), [all]);
  const [key, setKey] = useState<string | null>(() => planTypes.find((c) => !c.placeholder)?.key ?? custom[0]?.key ?? null);
  const chain = all.find((c) => c.key === key);
  const [values, setValues] = useState<ChainFormValues>(() => initialValues(chain?.inputs ?? []));
  const [json, setJson] = useState(() => jsonFor(chain));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [ran, setRan] = useState<{ title: string; stages: RunStage[]; input: Record<string, unknown>; platformRoute: string | null } | null>(null);
  const r = useChainRun();

  const pick = (c: ResolvedChain) => {
    if (c.placeholder || r.running) return;
    setKey(c.key);
    // Plan types share fields, so keep what the user typed where the keys match.
    const next = initialValues(c.inputs);
    for (const f of c.inputs) if (values[f.key] !== undefined && values[f.key] !== "" && f.type === "textarea") next[f.key] = values[f.key];
    setValues(next);
    setJson(jsonFor(c));
  };

  const typed = Boolean(chain && chain.inputs.length);
  const missing = chain && typed ? missingRequired(chain.inputs, values) : [];

  const run = async () => {
    if (!chain) return;
    let input: Record<string, unknown>;
    if (typed) input = chain.group === "meal_plan" && !chain.platformRoute ? withPlanRequirements(toInputData(chain.inputs, values)) : toInputData(chain.inputs, values);
    else {
      try {
        input = JSON.parse(json);
        setJsonError(null);
      } catch {
        setJsonError("Input must be valid JSON.");
        return;
      }
    }
    setRan({ title: chain.title, stages: typed ? planStages(chain, values.include_pdf !== false) : GENERIC_STAGES, input, platformRoute: chain.platformRoute ?? null });
    setShowRaw(false);
    await r.run(chain.key, input);
  };

  const result = r.execution ? mealPlanFromExecution(r.execution) : null;
  const tokens = r.execution ? runTokens(r.execution) : undefined;
  const pills = [...planTypes, ...custom];

  return (
    <RequireFeature feature="meal_plans">
      <PageHeader
        eyebrow="Passio API · POST /napi/tools/mealPlans/{type}"
        title="Meal plans"
        description="Describe what you want in plain language. Each plan type is a Passio API route that plans the days, writes recipes, links every ingredient to a Passio food and totals the macros. Every plan type is also a template in Easy AI Studio: start from it to change the steps, prompts or models, and tune quality and cost for your app."
      />
      {all.length === 0 ? (
        <EmptyState title="Meal-plan chains are coming soon" icon={<IconCalendarEvent size={32} color="var(--pd-green)" />}>
          Once the chains exist, an admin sets their ids under Admin → Chains and they appear here.
        </EmptyState>
      ) : (
        <Stack gap="lg">
          <Card p={{ base: "md", sm: "xl" }}>
            <Stack gap="lg">
              <div>
                <Eyebrow>Plan type</Eyebrow>
                <div className="pd-plan-pills" role="radiogroup" aria-label="Plan type" style={{ marginTop: 10 }}>
                  {pills.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className="pd-plan-pill"
                      role="radio"
                      aria-checked={c.key === key}
                      data-selected={c.key === key}
                      disabled={c.placeholder || r.running}
                      onClick={() => pick(c)}
                      title={c.description}
                    >
                      {c.title}
                      {c.placeholder && <span className="soon">Soon</span>}
                      {!c.group && <span className="soon">Custom</span>}
                    </button>
                  ))}
                </div>
                {chain?.description && (
                  <Text fz="sm" c="dimmed" mt="sm" maw={760}>
                    {chain.description}
                  </Text>
                )}
              </div>
              <Divider color="var(--pd-sand)" />
              {chain &&
                (typed ? (
                  <ChainForm fields={chain.inputs} values={values} onChange={setValues} examples={chain.examples} disabled={r.running} />
                ) : (
                  <Textarea label="input_data (JSON)" autosize minRows={5} styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }} value={json} onChange={(e) => setJson(e.currentTarget.value)} error={jsonError} />
                ))}
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Text fz="sm" c="dimmed">
                  {missing.length ? "Describe what you want to get started." : "Plans usually take 30–90 seconds."}
                </Text>
                <Button size="md" leftSection={<IconPlayerPlay size={16} />} onClick={run} loading={r.running} disabled={!chain || missing.length > 0}>
                  Generate plan
                </Button>
              </Group>
            </Stack>
          </Card>

          {r.running && ran && <RunProgressCard title="Building your plan" progress={r.progress} stages={ran.stages} onCancel={r.cancel} platformRoute={ran.platformRoute} />}
          <ChainError error={r.error} onRetry={run} />

          {r.execution && !r.running && (
            <>
              {result?.plan ? (
                <MealPlanView plan={result.plan} pdf={result.pdf} title={ran?.title ?? ""} household={result.household} />
              ) : (
                <Card>
                  <Title order={3} fz={18} mb="sm">
                    Result
                  </Title>
                  <JsonView value={sinkOutput(r.execution, chain?.sinkNodeId ?? undefined)} />
                </Card>
              )}
              <Group>
                <Button variant="subtle" color="gray" size="xs" leftSection={<IconCode size={14} />} onClick={() => setShowRaw((s) => !s)}>
                  {showRaw ? "Hide" : "Show"} request and raw execution
                </Button>
                {r.execution.execution_time_ms ? (
                  <Text fz="xs" c="dimmed" ff="monospace">
                    {(r.execution.execution_time_ms / 1000).toFixed(1)} s
                    {tokens !== undefined && ` · ${fmtNumber(tokens)} tokens`}
                  </Text>
                ) : null}
              </Group>
              <Collapse in={showRaw}>
                <Stack gap="sm">
                  <Text fz="sm" fw={650}>
                    input_data sent
                  </Text>
                  <JsonView value={ran?.input ?? {}} />
                  <Text fz="sm" fw={650}>
                    Execution
                  </Text>
                  <JsonView value={r.execution} />
                </Stack>
              </Collapse>
            </>
          )}
        </Stack>
      )}
    </RequireFeature>
  );
}
